// @flow
import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';
import PageTemplate from 'components/base/PageTemplate';
import MainMobileHeadContainer from 'containers/main/MainMobileHeadContainer';
import MainHeadContainer from 'containers/main/MainHeadContainer';
import { type ContextRouter } from 'react-router-dom';
import { CartActions, BaseActions } from 'store/actionCreators';
import './Chemical.scss';

const MEILISEARCH_URL = 'https://ms-ai.cacheby.com';
const MEILISEARCH_KEY = '8b302632c257a5524b97753d6abe0d731a802fc55500080145e1f7f44551f197';

type UploadedItem = {
  rowIndex: number,
  casNumber: string,
  catalogNumber: ?string,
  productName: ?string,
  brand: ?string,
  unit: ?string,
  quantity: ?number,
  currentPrice: ?number,
};

type ProductPrice = {
  id: string,
  unit: string,
  brand: string,
  price: number,
  status: string,
  cas_number: ?string,
  description: string,
  part_number: string,
};

type MatchedProduct = {
  id: string,
  title: string,
  url_slug: string,
  thumbnail: string,
  username: string,
  brand: string,
  price: number,
  min_price: number,
  max_price: number,
  cas_numbers: Array<string>,
  product_prices: Array<ProductPrice>,
};

type SearchResult = {
  ...UploadedItem,
  status: 'pending' | 'found' | 'not_found' | 'multiple',
  matchedProducts: Array<MatchedProduct>,
  bestMatch: ?MatchedProduct,
  exactMatch: ?ProductPrice,
  confidence: number,
  priceDiff: ?number,
  priceDiffPercent: ?number,
  isExpanded: boolean,
};

type State = {
  uploadedData: Array<UploadedItem>,
  searchResults: Array<SearchResult>,
  isProcessing: boolean,
  processingProgress: number,
  searchStartTime: ?number,
  totalSearchTime: ?number,
  isDragging: boolean,
  error: ?string,
  pageVisitTracked: boolean,
  lastFileSize: ?number,
  lastUploadDuration: ?number,
};

type Props = ContextRouter;

const validateCasNumber = (cas: string): boolean => {
  if (!cas) return false;
  const cleaned = cas.toString().trim();
  return /^\d{2,7}-\d{2}-\d$/.test(cleaned);
};

const formatPrice = (price: ?number): string => {
  if (!price || price <= 0) return '-';
  return `${Number(price).toLocaleString()}원`;
};

const formatPriceDiff = (diff: ?number, percent: ?number): { text: string, className: string } => {
  if (diff === null || diff === undefined) return { text: '-', className: '' };
  if (diff < 0) {
    return {
      text: `${Math.abs(diff).toLocaleString()}원 절감 (${Math.abs(percent || 0).toFixed(1)}%)`,
      className: 'price-savings',
    };
  }
  if (diff > 0) {
    return {
      text: `${diff.toLocaleString()}원 증가 (${(percent || 0).toFixed(1)}%)`,
      className: 'price-increase',
    };
  }
  return { text: '동일', className: 'price-same' };
};

class Chemical extends Component<Props, State> {
  fileInputRef: { current: null | HTMLInputElement };
  fileReadSpeedMBps: number = 0;
  abortController: ?AbortController = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      uploadedData: [],
      searchResults: [],
      isProcessing: false,
      processingProgress: 0,
      searchStartTime: null,
      totalSearchTime: null,
      isDragging: false,
      error: null,
      pageVisitTracked: false,
      lastFileSize: null,
      lastUploadDuration: null,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.trackEvent('chemical_page_visit', {});
    this.loadSavedResults();
    ReactTooltip.rebuild();
  }

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  loadSavedResults = () => {
    try {
      const saved = localStorage.getItem('chemical_search_results');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.setState({ searchResults: parsed });
      }
    } catch (e) {
      console.error('Failed to load saved results:', e);
    }
  };

  saveResults = (results: Array<SearchResult>) => {
    try {
      if (results.length > 500) {
        console.log('[Chemical] Results too large to save locally:', results.length);
        return;
      }
      localStorage.setItem('chemical_search_results', JSON.stringify(results));
    } catch (e) {
      console.log('[Chemical] LocalStorage full, skipping save');
    }
  };

  clearSavedResults = () => {
    try {
      localStorage.removeItem('chemical_search_results');
      this.setState({ searchResults: [], uploadedData: [] });
    } catch (e) {
      console.error('Failed to clear results:', e);
    }
  };

  trackEvent = (eventName: string, properties: Object) => {
    const timestamp = Date.now();
    const eventData = {
      event: eventName,
      timestamp,
      ...properties,
    };
    console.log('[Chemical Tracking]', eventData);

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, properties);
    }
  };

  handleDragOver = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDrop = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  };

  handleFileSelect = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  processFile = async (file: File) => {
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');
    const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');

    if (!isExcel && !isCsv) {
      this.setState({ error: '엑셀 파일(.xlsx, .xls) 또는 CSV 파일만 업로드 가능합니다.' });
      return;
    }

    this.trackEvent('chemical_upload_start', { fileName: file.name, fileSize: file.size });
    const uploadStartTime = Date.now();

    try {
      const fileReadStart = Date.now();
      const data = await file.arrayBuffer();
      const fileReadEnd = Date.now();
      const fileReadTime = fileReadEnd - fileReadStart || 1;
      const fileReadSpeedMBps = (file.size / (1024 * 1024)) / (fileReadTime / 1000);
      this.fileReadSpeedMBps = fileReadSpeedMBps;
      console.log(`[File Read] ${fileReadTime}ms, ${fileReadSpeedMBps.toFixed(1)}MB/초`);

      let jsonData;
      if (isExcel) {
        const parseStart = Date.now();
        const xlsxModule = await import(/* webpackChunkName: "xlsx" */ 'xlsx/dist/xlsx.full.min.js');
        const XLSX = xlsxModule.default || xlsxModule;
        const workbook = XLSX.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        console.log(`[Excel Parse] ${Date.now() - parseStart}ms`);
      } else {
        const parseStart = Date.now();
        const text = new TextDecoder().decode(data);
        const lines = text.split('\n');
        jsonData = [];
        lines.forEach(line => {
          if (line.trim()) {
            jsonData.push(line.split(','));
          }
        });
        console.log(`[CSV Parse] ${Date.now() - parseStart}ms`);
      }

      if (jsonData.length < 2) {
        this.setState({ error: '데이터가 없습니다. 헤더와 최소 1개의 데이터 행이 필요합니다.' });
        return;
      }

      const headers = jsonData[0].map((h: any) => String(h).toLowerCase().trim());
      const casIndex = headers.findIndex((h: string) =>
        h.includes('cas') || h.includes('번호') || h.includes('number'),
      );

      if (casIndex === -1) {
        this.setState({ error: 'CAS Number 컬럼을 찾을 수 없습니다.' });
        return;
      }

      const catalogIndex = headers.findIndex((h: string) =>
        h.includes('catalog') || h.includes('카탈로그') || h.includes('part') || h.includes('품번'),
      );
      const nameIndex = headers.findIndex((h: string) =>
        h.includes('product') || h.includes('name') || h.includes('제품') || h.includes('이름'),
      );
      const brandIndex = headers.findIndex((h: string) =>
        h.includes('brand') || h.includes('브랜드') || h.includes('제조사'),
      );
      const unitIndex = headers.findIndex((h: string) =>
        h.includes('unit') || h.includes('단위') || h.includes('규격'),
      );
      const quantityIndex = headers.findIndex((h: string) =>
        h.includes('quantity') || h.includes('수량') || h.includes('qty'),
      );
      const priceIndex = headers.findIndex((h: string) =>
        h.includes('price') || h.includes('가격') || h.includes('단가'),
      );

      const uploadedItems: Array<UploadedItem> = [];
      jsonData.slice(1).forEach((row, idx) => {
        if (!row || row.length === 0) return;
        const casNumber = row[casIndex] ? String(row[casIndex]).trim() : '';
        if (!casNumber) return;

        uploadedItems.push({
          rowIndex: idx + 1,
          casNumber,
          catalogNumber: catalogIndex >= 0 && row[catalogIndex] ? String(row[catalogIndex]).trim() : null,
          productName: nameIndex >= 0 && row[nameIndex] ? String(row[nameIndex]).trim() : null,
          brand: brandIndex >= 0 && row[brandIndex] ? String(row[brandIndex]).trim() : null,
          unit: unitIndex >= 0 && row[unitIndex] ? String(row[unitIndex]).trim() : null,
          quantity: quantityIndex >= 0 && row[quantityIndex] ? Number(row[quantityIndex]) : null,
          currentPrice: priceIndex >= 0 && row[priceIndex] ? Number(row[priceIndex]) : null,
        });
      });

      const uploadEndTime = Date.now();
      const uploadDuration = uploadEndTime - uploadStartTime;
      console.log(`[Total] ${uploadDuration}ms`);

      if (uploadedItems.length === 0) {
        this.setState({ error: '유효한 CAS 번호를 가진 데이터가 없습니다.' });
        return;
      }

      this.trackEvent('chemical_upload_complete', {
        fileName: file.name,
        fileSize: file.size,
        rowCount: uploadedItems.length,
        processingTimeMs: uploadDuration,
        fileReadSpeedMBps: fileReadSpeedMBps.toFixed(1),
      });

      this.setState(
        {
          uploadedData: uploadedItems,
          error: null,
          lastFileSize: file.size,
          lastUploadDuration: uploadDuration,
        },
        () => {
          this.performBatchSearch();
        },
      );
    } catch (err) {
      console.error('File upload error:', err);
      this.setState({ error: '파일을 업로드하는 중 오류가 발생했습니다.' });
    }
  };

  cancelSearch = () => {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    const { searchResults } = this.state;
    const completedResults = searchResults.filter(r => r.status !== 'pending');
    this.setState({
      isProcessing: false,
      processingProgress: 0,
      searchResults: completedResults,
    });
    this.trackEvent('chemical_search_cancelled', { completedCount: completedResults.length });
  };

  performBatchSearch = async () => {
    const { uploadedData, searchResults: existingResults } = this.state;
    if (uploadedData.length === 0) return;

    const existingCasNumbers = new Set(existingResults.map(r => r.casNumber));
    const newItems = uploadedData.filter(item => !existingCasNumbers.has(item.casNumber));

    if (newItems.length === 0) {
      this.setState({ error: '모든 CAS 번호가 이미 검색되었습니다.' });
      return;
    }

    const pendingResults = newItems.map(item => ({
      ...item,
      status: 'pending',
      matchedProducts: [],
      bestMatch: null,
      exactMatch: null,
      confidence: 0,
      priceDiff: null,
      priceDiffPercent: null,
      isExpanded: false,
    }));

    const searchStartTime = Date.now();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setState({
      isProcessing: true,
      processingProgress: 0,
      searchStartTime,
      searchResults: [...existingResults, ...pendingResults],
    });

    this.trackEvent('chemical_search_start', { totalItems: newItems.length });

    const BATCH_SIZE = 100;
    const totalItems = newItems.length;
    const batches = [];
    for (let i = 0; i < totalItems; i += BATCH_SIZE) {
      batches.push(newItems.slice(i, i + BATCH_SIZE));
    }

    let completed = 0;
    const batchPromises = batches.map(batch =>
      this.searchBatchByCasNumbers(batch, signal).then(batchResults => {
        if (signal.aborted) return [];
        completed += batch.length;
        const progress = Math.min(Math.round((completed / totalItems) * 100), 99);
        this.setState({ processingProgress: progress });
        return batchResults;
      })
    );

    try {
      const batchResultsArray = await Promise.all(batchPromises);
      if (signal.aborted) return;

      const results: Array<SearchResult> = batchResultsArray.flat();

      const totalSearchTime = Date.now() - searchStartTime;
      const matchedCount = results.filter(r => r.status === 'found' || r.status === 'multiple').length;
      const notFoundCount = results.filter(r => r.status === 'not_found').length;

      this.trackEvent('chemical_search_complete', {
        totalItems: newItems.length,
        matchedItems: matchedCount,
        notFoundItems: notFoundCount,
        processingTimeMs: totalSearchTime,
        matchRate: ((matchedCount / newItems.length) * 100).toFixed(2),
      });

      const mergedResults = [...existingResults, ...results];

      this.setState({
        isProcessing: false,
        processingProgress: 100,
        searchResults: mergedResults,
        totalSearchTime,
      });

      this.saveResults(mergedResults);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
      }
    } finally {
      this.abortController = null;
    }
  };

  searchBatchByCasNumbers = async (items: Array<UploadedItem>, signal?: AbortSignal): Promise<Array<SearchResult>> => {
    try {
      const queries = items.map(item => ({
        indexUid: 'products',
        q: item.casNumber,
        limit: 50,
        filter: `cas_numbers = "${item.casNumber}"`,
      }));

      const response = await fetch(`${MEILISEARCH_URL}/multi-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify({ queries }),
        signal,
      });

      const data = await response.json();
      const resultsArray = data.results || [];

      return items.map((item, idx) => {
        const result = resultsArray[idx];
        const matches = (result && result.hits) || [];
        return this.processSearchResult(item, matches);
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('Batch search error:', error);
      return items.map(item => this.processSearchResult(item, []));
    }
  };

  processSearchResult = (item: UploadedItem, matches: Array<MatchedProduct>): SearchResult => {
    if (matches.length === 0) {
      return {
        ...item,
        status: 'not_found',
        matchedProducts: [],
        bestMatch: null,
        exactMatch: null,
        confidence: 0,
        priceDiff: null,
        priceDiffPercent: null,
        isExpanded: false,
      };
    }

    const sortedMatches = [...matches].sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
    const bestMatch = sortedMatches[0];

    let exactMatch = null;
    if (item.catalogNumber) {
      const catalogNum = item.catalogNumber.toLowerCase();
      sortedMatches.some(product => {
        if (product.product_prices) {
          const found = product.product_prices.find(
            p => p.part_number && p.part_number.toLowerCase() === catalogNum
          );
          if (found) {
            exactMatch = found;
            return true;
          }
        }
        return false;
      });
    }

    let priceDiff = null;
    let priceDiffPercent = null;
    if (item.currentPrice) {
      const comparePrice = exactMatch ? exactMatch.price : bestMatch.min_price;
      if (comparePrice) {
        priceDiff = comparePrice - item.currentPrice;
        priceDiffPercent = (priceDiff / item.currentPrice) * 100;
      }
    }

    const confidence = exactMatch ? 100 : 100;

    return {
      ...item,
      status: matches.length === 1 ? 'found' : 'multiple',
      matchedProducts: sortedMatches,
      bestMatch,
      exactMatch,
      confidence,
      priceDiff,
      priceDiffPercent,
      isExpanded: false,
    };
  };

  toggleRowExpansion = (index: number) => {
    this.setState(prevState => {
      const newResults = [...prevState.searchResults];
      newResults[index] = {
        ...newResults[index],
        isExpanded: !newResults[index].isExpanded,
      };
      return { searchResults: newResults };
    });

    const result = this.state.searchResults[index];
    if (!result.isExpanded) {
      this.trackEvent('chemical_substitute_click', {
        casNumber: result.casNumber,
        substituteCount: result.matchedProducts.length,
        rankPosition: index + 1,
      });
    }
  };

  handleDeleteRow = (index: number) => {
    const { searchResults } = this.state;
    const newResults = searchResults.filter((_, i) => i !== index);
    this.setState({ searchResults: newResults });
    this.saveResults(newResults);
  };

  handleProductClick = (result: SearchResult, product: MatchedProduct, rankPosition: number) => {
    this.trackEvent('chemical_result_click', {
      casNumber: result.casNumber,
      productId: product.id,
      productTitle: product.title,
      brand: product.brand,
      minPrice: product.min_price,
      rankPosition,
      isSubstitute: result.matchedProducts.length > 1,
    });
  };

  handleProductSelect = (result: SearchResult, product: MatchedProduct, rankPosition: number) => {
    window.open(`/@${product.username}/products/${product.id}`, '_blank');
    this.trackEvent('chemical_product_select', {
      casNumber: result.casNumber,
      selectedProductId: product.id,
      productTitle: product.title,
      brand: product.brand,
      minPrice: product.min_price,
      rankPosition,
      totalOptions: result.matchedProducts.length,
      isSubstitute: result.matchedProducts.length > 1,
    });
  };

  downloadTemplate = () => {
    const templateData = [
      ['CAS Number', 'Catalog Number', 'Product Name', 'Brand', 'Unit', 'Quantity', 'Current Price'],
      ['7732-18-5', 'R097811-1L', 'Water', 'Duksan', '1L', 10, 50000],
      ['64-17-5', 'R003460-500ml', 'Ethanol', 'Duksan', '500ml', 5, 80000],
      ['67-56-1', 'R094769-1L', 'Methanol', 'Duksan', '1L', 3, 75000],
    ];

    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'chemical_search_template.csv';
    link.click();

    this.trackEvent('chemical_template_download', {});
  };

  handleAddToCart = async (product: MatchedProduct, price: ProductPrice) => {
    try {
      await CartActions.addItemCart({
        productId: product.id,
        productPriceId: price.id,
        quantity: 1,
      });
      await CartActions.getCart();

      BaseActions.showToast({
        type: 'success',
        message: '장바구니에 담았습니다.',
      });

      this.trackEvent('chemical_add_to_cart', {
        productId: product.id,
        productTitle: product.title,
        priceId: price.id,
        partNumber: price.part_number,
        brand: price.brand,
        price: price.price,
      });
    } catch (e) {
      console.error('장바구니 담기 실패:', e);
      BaseActions.showToast({
        type: 'error',
        message: '장바구니 담기에 실패했습니다. 로그인이 필요할 수 있습니다.',
      });
    }
  };

  exportResults = () => {
    const { searchResults } = this.state;
    if (searchResults.length === 0) return;

    const exportData = [
      [
        '검색 CAS Number',
        '검색 제품명',
        '검색 브랜드',
        '검색 현재가격',
        '검색 상태',
        '매칭 상품명',
        '매칭 상품 브랜드',
        '매칭 상품 최저가',
        '매칭 상품 최고가',
        '매칭 상품 URL',
        '옵션 Part Number',
        '옵션 브랜드',
        '옵션 설명',
        '옵션 단위',
        '옵션 가격',
        '옵션 CAS Number',
        '가격차이',
        '가격차이(%)',
      ],
    ];

    searchResults.forEach(result => {
      if (result.matchedProducts.length === 0) {
        exportData.push([
          result.casNumber,
          result.productName || '',
          result.brand || '',
          result.currentPrice || '',
          '미매칭',
          '', '', '', '', '', '', '', '', '', '', '', '', '',
        ]);
      } else {
        result.matchedProducts.forEach(product => {
          if (product.product_prices && product.product_prices.length > 0) {
            product.product_prices.forEach(price => {
              const priceDiff = result.currentPrice && price.price
                ? price.price - result.currentPrice
                : '';
              const priceDiffPercent = result.currentPrice && price.price
                ? ((price.price - result.currentPrice) / result.currentPrice * 100).toFixed(2)
                : '';

              exportData.push([
                result.casNumber,
                result.productName || '',
                result.brand || '',
                result.currentPrice || '',
                '매칭됨',
                product.title || '',
                product.brand || '',
                product.min_price || '',
                product.max_price || '',
                `/@${product.username}/products/${product.id}`,
                price.part_number || '',
                price.brand || '',
                price.description || '',
                price.unit || '',
                price.price || '',
                price.cas_number || '',
                priceDiff,
                priceDiffPercent,
              ]);
            });
          } else {
            exportData.push([
              result.casNumber,
              result.productName || '',
              result.brand || '',
              result.currentPrice || '',
              '매칭됨',
              product.title || '',
              product.brand || '',
              product.min_price || '',
              product.max_price || '',
              `/@${product.username}/products/${product.id}`,
              '', '', '', '', '', '',
              result.priceDiff || '',
              result.priceDiffPercent ? result.priceDiffPercent.toFixed(2) : '',
            ]);
          }
        });
      }
    });

    const csvContent = exportData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `chemical_search_results_${Date.now()}.csv`;
    link.click();

    this.trackEvent('chemical_export_complete', {
      totalRows: searchResults.length,
      matchedRows: searchResults.filter(r => r.status !== 'not_found').length,
    });
  };

  renderUploadArea = () => {
    const { isProcessing, isDragging } = this.state;

    return (
      <div className="upload-section">
        <div className="upload-header">
          <span className="upload-badge">AI-Powered</span>
          <h1 className="upload-title">엑셀 업로드형 AI 비교 추천 서비스</h1>
          <p className="upload-description">
            CAS 번호가 포함된 엑셀 파일을 업로드하면<br />
            AI가 383만+ 품목에서 최적의 대체품과 최저가를 찾아드립니다.
          </p>
        </div>

        <div
          className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${isProcessing ? 'processing' : ''}`}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
          onClick={() => !isProcessing && this.fileInputRef.current && this.fileInputRef.current.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (!isProcessing && this.fileInputRef.current) {
                this.fileInputRef.current.click();
              }
            }
          }}
        >
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={this.handleFileSelect}
            disabled={isProcessing}
          />

          {isProcessing ? (
            <div className="upload-processing">
              <div className="spinner" />
              <span className="processing-text">파일 처리 중...</span>
            </div>
          ) : (
            <React.Fragment>
              <div className="upload-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M24 4L24 32M24 4L16 12M24 4L32 12"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 28V40C8 41.1046 8.89543 42 10 42H38C39.1046 42 40 41.1046 40 40V28"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="upload-text">
                <span className="upload-main">파일을 드래그하거나 클릭하여 업로드</span>
                <span className="upload-sub">Excel (.xlsx, .xls) 또는 CSV 파일 지원</span>
              </div>
            </React.Fragment>
          )}

          {isDragging && (
            <div className="drag-overlay">
              <span>여기에 파일을 놓으세요</span>
            </div>
          )}
        </div>

        <div className="upload-actions">
          <button
            type="button"
            className="template-btn"
            onClick={e => {
              e.stopPropagation();
              this.downloadTemplate();
            }}
            disabled={isProcessing}
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            템플릿 다운로드
          </button>
          <span className="template-hint">처음이신가요? 샘플 템플릿을 다운로드하세요</span>
        </div>

        <div className="upload-features">
          <div className="feature-item">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="feature-content">
              <span className="feature-title">CAS 번호 자동 인식</span>
              <span className="feature-desc">엑셀의 CAS 번호 컬럼을 자동으로 찾습니다</span>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="feature-content">
              <span className="feature-title">빠른 검색</span>
              <span className="feature-desc">수백 개의 CAS 번호를 몇 초 만에 검색</span>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="feature-content">
              <span className="feature-title">가격 비교</span>
              <span className="feature-desc">기존 가격과 최저가를 한눈에 비교</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderSummary = () => {
    const { searchResults, totalSearchTime, isProcessing, processingProgress, lastFileSize, lastUploadDuration } = this.state;
    if (searchResults.length === 0) return null;

    const totalCount = searchResults.length;
    const fileSizeMB = lastFileSize ? lastFileSize / (1024 * 1024) : 0;
    const uploadSpeedMBps = lastUploadDuration && lastUploadDuration > 0
      ? fileSizeMB / (lastUploadDuration / 1000)
      : 0;
    const matchedCount = searchResults.filter(r => r.status === 'found' || r.status === 'multiple').length;
    const notFoundCount = searchResults.filter(r => r.status === 'not_found').length;
    const pendingCount = searchResults.filter(r => r.status === 'pending').length;

    const totalSavings = searchResults.reduce((sum, r) => {
      if (r.priceDiff && r.priceDiff < 0) {
        return sum + Math.abs(r.priceDiff);
      }
      return sum;
    }, 0);

    return (
      <div className="chemical-summary">
        <div className="summary-item">
          <span className="summary-label">전체</span>
          <span className="summary-value">{totalCount}건</span>
        </div>
        <div className="summary-item matched">
          <span className="summary-label">매칭</span>
          <span className="summary-value">{matchedCount}건</span>
        </div>
        <div className="summary-item not-found">
          <span className="summary-label">미매칭</span>
          <span className="summary-value">{notFoundCount}건</span>
        </div>
        {this.fileReadSpeedMBps > 0 && (
          <div className="summary-item">
            <span className="summary-label">
              파일처리
              <span
                className="info-icon"
                data-tip="파일 읽기 속도입니다. 작은 파일(100KB 미만)은 측정 오버헤드로 인해 실제보다 낮게 표시될 수 있습니다. 1MB 이상 파일에서 정확한 속도를 확인할 수 있습니다."
                data-for="chemical-tooltip"
              >?</span>
            </span>
            <span className="summary-value">{this.fileReadSpeedMBps.toFixed(1)}MB/초</span>
          </div>
        )}
        {isProcessing && (
          <div className="summary-item processing">
            <span className="summary-label">검색중</span>
            <span className="summary-value">{processingProgress}%</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${processingProgress}%` }} />
            </div>
            <button
              className="cancel-btn"
              onClick={this.cancelSearch}
              type="button"
            >
              취소
            </button>
          </div>
        )}
        {totalSavings > 0 && (
          <div className="summary-item savings">
            <span className="summary-label">예상 절감액</span>
            <span className="summary-value">{totalSavings.toLocaleString()}원</span>
          </div>
        )}
        <button
          className="export-btn"
          onClick={this.exportResults}
          disabled={isProcessing || pendingCount > 0}
          data-tip="검색 결과를 CSV 파일로 다운로드합니다. 모든 대체품 정보가 포함됩니다"
          data-for="chemical-tooltip"
        >
          결과 내보내기
        </button>
        <button
          className="clear-btn"
          onClick={this.clearSavedResults}
          disabled={isProcessing}
          data-tip="저장된 모든 검색 결과를 삭제합니다"
          data-for="chemical-tooltip"
        >
          전체 삭제
        </button>
      </div>
    );
  };

  renderResultsTable = () => {
    const { searchResults, isProcessing } = this.state;
    if (searchResults.length === 0) return null;

    return (
      <div className="chemical-results">
        <table className="chemical-table">
          <thead>
            <tr>
              <th className="th-expand" />
              <th className="th-cas">
                CAS 번호
                <span className="info-icon" data-tip="Chemical Abstracts Service 등록 번호로 화학물질 고유 식별자입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-catalog">
                카탈로그 번호
                <span className="info-icon" data-tip="제품 고유 카탈로그/파트 번호입니다. 동일 번호가 있으면 정확히 같은 제품입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-name">제품명</th>
              <th className="th-brand">브랜드</th>
              <th className="th-current-price">
                기존 가격
                <span className="info-icon" data-tip="업로드한 엑셀에 입력된 현재 구매 가격입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-best-price">
                최저 가격
                <span className="info-icon" data-tip="동일 CAS 번호 제품 중 가장 낮은 가격입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-diff">
                가격 비교
                <span className="info-icon" data-tip="기존 가격 대비 절감/증가 금액입니다. 마이너스는 절감을 의미합니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-confidence">
                정확도
                <span className="info-icon" data-tip="CAS 번호 일치 정확도입니다. 100%는 정확히 일치함을 의미합니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-substitutes">
                대체품
                <span className="info-icon" data-tip="동일 CAS 번호를 가진 다른 공급업체 제품 수입니다" data-for="chemical-tooltip">?</span>
              </th>
              <th className="th-delete" />
            </tr>
          </thead>
          <tbody>
            {searchResults.map((result, idx) => (
              <React.Fragment key={result.rowIndex}>
                <tr className={`result-row status-${result.status}`}>
                  <td className="td-expand">
                    {result.matchedProducts.length > 0 && (
                      <button
                        className={`expand-btn ${result.isExpanded ? 'expanded' : ''}`}
                        onClick={() => this.toggleRowExpansion(idx)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    )}
                  </td>
                  <td className="td-cas">{result.casNumber}</td>
                  <td className="td-catalog">
                    {result.catalogNumber || '-'}
                    {result.exactMatch && (
                      <span className="exact-match-badge" data-tip="카탈로그 번호가 일치하는 동일 제품입니다" data-for="chemical-tooltip">동일제품</span>
                    )}
                  </td>
                  <td className="td-name">{result.productName || '-'}</td>
                  <td className="td-brand">{result.brand || '-'}</td>
                  <td className="td-current-price">{formatPrice(result.currentPrice)}</td>
                  <td className="td-best-price">
                    {result.exactMatch
                      ? formatPrice(result.exactMatch.price)
                      : result.bestMatch
                      ? formatPrice(result.bestMatch.min_price)
                      : '-'}
                  </td>
                  <td className={`td-diff ${formatPriceDiff(result.priceDiff, result.priceDiffPercent).className}`}>
                    {formatPriceDiff(result.priceDiff, result.priceDiffPercent).text}
                  </td>
                  <td className="td-confidence">
                    {result.status === 'pending' ? (
                      <span className="loading-dot" />
                    ) : result.status === 'not_found' ? (
                      '-'
                    ) : (
                      `${result.confidence}%`
                    )}
                  </td>
                  <td className="td-substitutes">
                    {result.matchedProducts.length > 0 ? (
                      <span className="substitute-count">{result.matchedProducts.length}개</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="td-delete">
                    <button
                      className="delete-row-btn"
                      onClick={() => this.handleDeleteRow(idx)}
                      title="삭제"
                    >
                      ×
                    </button>
                  </td>
                </tr>
                {result.isExpanded && result.matchedProducts.length > 0 && (
                  <tr className="expanded-row">
                    <td colSpan={11}>
                      <div className="substitutes-list">
                        <h4>대체품 목록 ({result.matchedProducts.length}개)</h4>
                        {result.matchedProducts.map((product, pIdx) => (
                          <div key={product.id} className="product-card">
                            <div className="product-header">
                              <a
                                href={`/@${product.username}/products/${product.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="product-title"
                              >
                                {product.title || '-'}
                              </a>
                              <span className="product-brand">{product.brand || '-'}</span>
                              <span className="product-price-range">
                                {formatPrice(product.min_price)}
                                {product.max_price !== product.min_price && ` ~ ${formatPrice(product.max_price)}`}
                              </span>
                              {pIdx === 0 && <span className="best-badge">최저가</span>}
                            </div>
                            {product.product_prices && product.product_prices.length > 0 && (
                              <table className="items-table">
                                <thead>
                                  <tr>
                                    <th>CAS Number</th>
                                    <th>Part Number</th>
                                    <th>브랜드</th>
                                    <th>설명</th>
                                    <th>단위</th>
                                    <th>가격</th>
                                    <th>가격비교</th>
                                    <th>액션</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {product.product_prices.map(price => {
                                    const isExactMatch = result.catalogNumber &&
                                      price.part_number &&
                                      price.part_number.toLowerCase() === result.catalogNumber.toLowerCase();
                                    const itemPriceDiff = result.currentPrice && price.price
                                      ? price.price - result.currentPrice
                                      : null;
                                    const itemPriceDiffPercent = result.currentPrice && itemPriceDiff !== null
                                      ? (itemPriceDiff / result.currentPrice) * 100
                                      : null;

                                    return (
                                      <tr key={price.id} className={isExactMatch ? 'exact-match-row' : ''}>
                                        <td className="td-cas">{price.cas_number || '-'}</td>
                                        <td className="td-part-number">
                                          {price.part_number || '-'}
                                          {isExactMatch && <span className="exact-badge">동일</span>}
                                        </td>
                                        <td>{price.brand || '-'}</td>
                                        <td className="td-description">{price.description || '-'}</td>
                                        <td>{price.unit || '-'}</td>
                                        <td className="td-price">{formatPrice(price.price)}</td>
                                        <td className={`td-price-diff ${itemPriceDiff !== null ? (itemPriceDiff < 0 ? 'savings' : itemPriceDiff > 0 ? 'increase' : '') : ''}`}>
                                          {itemPriceDiff !== null ? (
                                            itemPriceDiff < 0
                                              ? <span>-{Math.abs(itemPriceDiff).toLocaleString()}원 ({Math.abs(itemPriceDiffPercent || 0).toFixed(1)}%)</span>
                                              : itemPriceDiff > 0
                                              ? <span>+{itemPriceDiff.toLocaleString()}원 ({(itemPriceDiffPercent || 0).toFixed(1)}%)</span>
                                              : <span>동일</span>
                                          ) : '-'}
                                        </td>
                                        <td>
                                          <button
                                            className="chemical-cart-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              this.handleAddToCart(product, price);
                                            }}
                                            data-tip="이 상품을 장바구니에 담습니다"
                                            data-for="chemical-tooltip"
                                          >
                                            장바구니
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const { searchResults, error } = this.state;
    const hasResults = searchResults.length > 0;

    return (
      <PageTemplate>
        <header>
          <MainHeadContainer />
          <MainMobileHeadContainer />
        </header>
        <div className="chemical-page">
          <div className="chemical-container">
            {this.renderUploadArea()}

            {error && (
              <div className="chemical-error">
                <span>{error}</span>
                <button onClick={() => this.setState({ error: null })}>×</button>
              </div>
            )}

            {hasResults && (
              <React.Fragment>
                {this.renderSummary()}
                {this.renderResultsTable()}
              </React.Fragment>
            )}
          </div>
        </div>
        <ReactTooltip
          id="chemical-tooltip"
          place="top"
          effect="solid"
          className="chemical-tooltip"
          delayShow={200}
        />
      </PageTemplate>
    );
  }
}

export default Chemical;



// WEBPACK FOOTER //
// ./src/pages/Chemical.js